import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Eye, ArrowRight, Sparkles, Clock, Copy, Shuffle, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { trackBlueprintRemixed, trackSharedBlueprintViewed } from '../utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlueprintCard = ({ blueprint, index, onClick, onRemix }) => {
    const [copied, setCopied] = useState(false);

    const handleCopyPrompt = (e) => {
        e.stopPropagation();
        const prompt = `Build ${blueprint.idea}`;
        navigator.clipboard.writeText(prompt);
        setCopied(true);
        toast.success('Prompt copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
    };

    const handleRemix = (e) => {
        e.stopPropagation();
        onRemix(blueprint.idea);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="group relative bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden hover:border-violet-500/50 hover:shadow-[0_0_20px_rgba(124,58,237,0.15)] transition-all duration-300"
            data-testid={`featured-blueprint-${blueprint.short_id}`}
        >
            {/* Main clickable area */}
            <button
                onClick={onClick}
                className="w-full text-left p-4 pb-14"
            >
                <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="font-medium text-white group-hover:text-violet-300 transition-colors line-clamp-1">
                        {blueprint.app_name}
                    </h4>
                    <div className="flex items-center gap-1 text-xs text-zinc-500 flex-shrink-0">
                        <Eye className="w-3 h-3" />
                        <span>{blueprint.views}</span>
                    </div>
                </div>
                <p className="text-sm text-zinc-400 line-clamp-2 mb-2">
                    {blueprint.summary || blueprint.idea}
                </p>
                <div className="flex items-center text-xs text-violet-400 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>View full blueprint</span>
                    <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                </div>
            </button>

            {/* Action buttons */}
            <div className="absolute bottom-0 left-0 right-0 p-3 pt-0 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-zinc-900 to-transparent">
                <button
                    onClick={handleCopyPrompt}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-lg text-zinc-300 hover:text-white transition-all"
                    title="Copy prompt"
                >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied!' : 'Copy Prompt'}
                </button>
                <button
                    onClick={handleRemix}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs bg-violet-600/20 hover:bg-violet-600/30 border border-violet-500/30 rounded-lg text-violet-300 hover:text-white transition-all"
                    title="Remix this idea"
                >
                    <Shuffle className="w-3 h-3" />
                    Remix
                </button>
            </div>
        </motion.div>
    );
};

const TabButton = ({ active, onClick, icon: Icon, label }) => (
    <button
        onClick={onClick}
        className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-all ${
            active 
                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30' 
                : 'text-zinc-400 hover:text-zinc-300 hover:bg-zinc-800/50'
        }`}
        data-testid={`tab-${label.toLowerCase().replace(' ', '-')}`}
    >
        <Icon className="w-3.5 h-3.5" />
        {label}
    </button>
);

export const FeaturedBlueprints = ({ onViewBlueprint, onRemixIdea }) => {
    const [activeTab, setActiveTab] = useState('featured');
    const [blueprints, setBlueprints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlueprints = async () => {
            setLoading(true);
            try {
                const endpoint = activeTab === 'featured' ? 'featured' : 'recent';
                const response = await axios.get(`${API}/${endpoint}?limit=6`);
                setBlueprints(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch blueprints:', err);
                setError('Failed to load blueprints');
            } finally {
                setLoading(false);
            }
        };

        fetchBlueprints();
    }, [activeTab]);

    const handleRemix = (idea) => {
        if (onRemixIdea) {
            // Remixes from Featured/Recent section are NOT from a shared view
            // (shared view is only when visiting /b/{shortId} directly)
            trackBlueprintRemixed(idea, false);
            onRemixIdea(idea);
            // Scroll to top smoothly to show the prompt
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleViewBlueprint = (shortId, appName) => {
        trackSharedBlueprintViewed(shortId, appName);
        onViewBlueprint(shortId);
    };

    // Don't render if no blueprints and not loading
    if (!loading && blueprints.length === 0) {
        return null;
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
            data-testid="featured-blueprints-section"
        >
            {/* Header with tabs */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-violet-500/20">
                        <Sparkles className="w-4 h-4 text-violet-400" />
                    </div>
                    <h3 className="text-lg font-medium text-white">Explore Apps Built With Vibe Coding Coach</h3>
                </div>

                {/* Tab switcher */}
                <div className="flex items-center gap-2">
                    <TabButton 
                        active={activeTab === 'featured'}
                        onClick={() => setActiveTab('featured')}
                        icon={Sparkles}
                        label="Featured"
                    />
                    <TabButton 
                        active={activeTab === 'recent'}
                        onClick={() => setActiveTab('recent')}
                        icon={Clock}
                        label="Most Recent"
                    />
                </div>
            </div>

            {/* Loading state */}
            {loading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl animate-pulse">
                            <div className="h-5 bg-zinc-800 rounded w-3/4 mb-3"></div>
                            <div className="h-4 bg-zinc-800 rounded w-full mb-2"></div>
                            <div className="h-4 bg-zinc-800 rounded w-2/3"></div>
                        </div>
                    ))}
                </div>
            )}

            {/* Blueprint grid */}
            {!loading && !error && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {blueprints.map((blueprint, index) => (
                        <BlueprintCard
                            key={blueprint.short_id}
                            blueprint={blueprint}
                            index={index}
                            onClick={() => handleViewBlueprint(blueprint.short_id, blueprint.app_name)}
                            onRemix={handleRemix}
                        />
                    ))}
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && blueprints.length === 0 && (
                <div className="text-center py-8 text-zinc-500">
                    <p>No blueprints yet. Be the first to share!</p>
                </div>
            )}
        </motion.div>
    );
};
